import { useReducer, ChangeEvent, useState, useEffect, useMemo } from 'react'
import { Page } from 'Common/Page/Page'
import { Button, DropDown, PageHeader } from 'Common/Supplyvue-ui'
import { Box, SelectChangeEvent, Typography } from '@mui/material'
import {
  Config,
  FormActions,
  FormState,
  SalesRatePeriodOptions,
} from 'Features/Projects/Scenarios/EditSettings/PortfolioSettings/SalesRate.types'
import {
  useCreateScenarioConfig,
  useSalesRate,
  useUpdateSalesRate,
} from '../../Scenarios.data'
import { useParams } from 'react-router-dom'
import { FORECAST, HISTORICAL } from '../../CreateScenario.page'
import { DatePickerMUI } from '../../Calender'
import dayjs, { Dayjs } from 'dayjs'
import { Loader } from 'Common/Loader/Loader'
import { toast } from 'react-hot-toast'

export const INITIAL_FORM_STATE: FormState = {
  // TODO: Replace the initial State with the current stored
  //  values for sales Rate Period Type, Period Start Week, Number of Weeks
  salesRatePeriodType: HISTORICAL,
  periodStartWeek: dayjs(),
  numberOfWeeks: 1,
}

export const formReducer = <T extends keyof FormState>(
  state: FormState,
  action: FormActions<T>
): FormState => {
  switch (action.type) {
    case 'UPDATE_FIELD':
      return { ...state, [action.field]: action.value }
    case 'RESET_FORM':
      return INITIAL_FORM_STATE
    default:
      return state
  }
}

const useFormState = () => {
  return useReducer(formReducer, INITIAL_FORM_STATE)
}

type MenuItem = {
  value: string | number
  label: string
}

const salesRateTypeDropDownValues: MenuItem[] = [
  {
    value: HISTORICAL,
    label: 'Historical',
  },
  {
    value: FORECAST,
    label: 'Forecast',
  },
]

const config: Config = {
  maxDate: dayjs(),
  minDate: dayjs(),
  numWeeks: 0,
}

export const SalesRateView = () => {
  const [state, dispatch] = useFormState()
  const { projectId, scenarioId } = useParams<{
    projectId: string
    scenarioId: string
  }>()
  const { data, isLoading } = useCreateScenarioConfig(
    projectId as string,
    state.salesRatePeriodType
  )

  const { data: salesRateData, isPending } = useSalesRate(projectId, scenarioId)

  const [configData, setConfigData] = useState(config)

  useEffect(() => {
    if (data) {
      const maxDate = dayjs(data.maxDate)
      const minDate = dayjs(data.minDate)
      setConfigData({
        maxDate: maxDate,
        minDate: minDate,
        numWeeks: data.numWeeks,
      })
      dispatch({
        type: 'UPDATE_FIELD',
        field: 'periodStartWeek',
        value: minDate,
      })
    }
  }, [data])

  useEffect(() => {
    if (salesRateData) {
      dispatch({
        type: 'UPDATE_FIELD',
        field: 'salesRatePeriodType',
        value: salesRateData.salesRatePeriodType,
      })
      dispatch({
        type: 'UPDATE_FIELD',
        field: 'periodStartWeek',
        value: dayjs(salesRateData.periodStartWeek),
      })
      dispatch({
        type: 'UPDATE_FIELD',
        field: 'numberOfWeeks',
        value: salesRateData.numberOfWeeks,
      })
    }
  }, [salesRateData])

  const weekSelectionDropDown = useMemo(() => {
    const weeks = []
    const startWeek = state.periodStartWeek
    const endweek = configData.maxDate
    const diff = endweek.diff(startWeek, 'week')
    for (let i = 1; i <= diff; i++) {
      weeks.push({
        value: i,
        label: `${i} weeks`,
      })
    }
    return weeks
  }, [state.periodStartWeek, configData.maxDate])

  const handleChange = (
    e: SelectChangeEvent<{ name?: string; value: unknown }>
  ) => {
    dispatch({
      type: 'UPDATE_FIELD',
      field: e.target.name as keyof FormState,
      value: e.target.value as number | SalesRatePeriodOptions | Dayjs,
    })
  }

  const handleCancel = () => {
    dispatch({
      type: 'RESET_FORM',
    })
  }
  const updateSalesRateSuccess = () => {
    toast.success('Sales rate updated successfully')
  }
  const updateSalesRateFailure = () => {
    toast.error('Failed to update sales rate')
  }
  const { mutate: updateSalesRate, isPending: isUpdating } = useUpdateSalesRate(
    updateSalesRateSuccess,
    updateSalesRateFailure
  )

  const handleUpdateSalesRate = () => {
    const updateParams = {
      projectId: projectId as string,
      scenarioId: scenarioId as string,
      data: {
        ...state,
      },
    }
    updateSalesRate(updateParams)
  }
  if (isLoading || isUpdating) {
    return <Loader />
  }

  return (
    <Page>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 'auto',
          minHeight: '450px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            width: '100%',
          }}
        >
          <PageHeader title="Sales Rate" />
        </Box>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              height: '350px',
              width: '700px',
              padding: '36px 32px',
              gap: '16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                gap: '16px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '15rem' }}>
                  <Typography variant="h6">Sales rate period type</Typography>
                </Box>
                <Box sx={{ width: '18rem' }}>
                  <DropDown
                    fullWidth
                    label={'SalesRatePeriodType'}
                    name="salesRatePeriodType"
                    value={state.salesRatePeriodType}
                    onChange={handleChange}
                    menuItems={salesRateTypeDropDownValues}
                    defaultValue={HISTORICAL}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '15rem' }}>
                  <Typography variant="h6">Period start week</Typography>
                </Box>
                <Box sx={{ width: '18rem' }}>
                  <DatePickerMUI
                    sx={{ width: '100%' }}
                    fullWidth
                    label="Period start week"
                    value={state.periodStartWeek}
                    onChange={(value: Dayjs) =>
                      // handleCalendarInputChange('periodStartWeek', value)
                      dispatch({
                        type: 'UPDATE_FIELD',
                        field: 'periodStartWeek',
                        value: value,
                      })
                    }
                    format="DD-MM-YYYY"
                    minDate={configData.minDate}
                    maxDate={configData.maxDate}
                    shouldDisableDate={(date: Dayjs) => date.day() !== 1}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box sx={{ width: '15rem' }}>
                  <Typography variant="h6">Number of weeks</Typography>
                </Box>
                <Box sx={{ width: '18rem' }}>
                  <DropDown
                    fullWidth
                    id="number-of-weeks"
                    name="number-of-weeks"
                    menuItems={weekSelectionDropDown}
                    label="Number of weeks"
                    defaultValue={1}
                    onChange={(event) =>
                      dispatch({
                        type: 'UPDATE_FIELD',
                        field: 'numberOfWeeks',
                        value: event.target.value as number,
                      })
                    }
                    value={state.numberOfWeeks}
                  />
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: '8px',
              }}
            >
              <Button variant="contained" onClick={handleUpdateSalesRate}>
                Apply
              </Button>
              <Button variant="outlined" onClick={handleCancel}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Page>
  )
}
